import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { CCard, CCardBody, CCardHeader, CCol, CRow } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import ReactTimeAgo from "react-time-ago";
import moment from "moment";

import WidgetsDropdown from "../widgets/WidgetsDropdown.js";
import { APIS } from "../../config";

// Constants
const DASHBOARD_REFRESH_INTERVAL_MS = 10000; // 10 seconds
const MAX_BLOCKS_TO_DISPLAY = 6;
const MAX_TRANSACTIONS_TO_DISPLAY = 5;
const METAMUI_TOKEN_DECIMALS = 1e6;

class Dashboard extends Component {
  state = {
    recentBlocksList: [],
    recentTransactionsList: [],
    dataRefreshIntervalId: null,
  };

  componentDidMount() {
    this.loadDashboardData();
    const dataRefreshIntervalId = setInterval(this.loadDashboardData, DASHBOARD_REFRESH_INTERVAL_MS);
    this.setState({ dataRefreshIntervalId });
  }

  componentWillUnmount() {
    if (this.state.dataRefreshIntervalId) {
      clearInterval(this.state.dataRefreshIntervalId);
    }
  }

  getAuthenticationHeaders = () => {
    const userAuthToken = sessionStorage.getItem('login');
    return userAuthToken ? { Authorization: userAuthToken } : {};
  }

  fetchApiEndpointData = async (apiEndpoint) => {
    const authHeaders = this.getAuthenticationHeaders();
    const apiResponse = await fetch(APIS + apiEndpoint, { headers: authHeaders });
    return apiResponse.json();
  }

  loadDashboardData = async () => {
    try {
      const [blocksApiResponse, transactionsApiResponse] = await Promise.all([
        this.fetchApiEndpointData("block"),
        this.fetchApiEndpointData("balances/transfer")
      ]);

      const latestBlocksData = blocksApiResponse.data.slice(0, MAX_BLOCKS_TO_DISPLAY);
      const latestTransactionsData = transactionsApiResponse.data.slice(0, MAX_TRANSACTIONS_TO_DISPLAY);

      this.setState({
        recentBlocksList: latestBlocksData,
        recentTransactionsList: latestTransactionsData
      });
    } catch (dashboardLoadError) {
      console.error('Error loading dashboard data:', dashboardLoadError);
    }
  }

  navigateToBlockDetailsPage = (selectedBlockId) => {
    this.props.history.push(`/block/${selectedBlockId}`);
  }

  navigateToTransactionDetailsPage = (selectedTransactionId) => {
    const selectedTokenName = this.props?.selectedToken?.name || 'MetaMUI';
    this.props.history.push(`/tnx-details/${selectedTransactionId}/${selectedTokenName}`);
  }

  renderBlockTableRow = (blockData, rowIndex) => (
    <tr key={rowIndex} onClick={() => this.navigateToBlockDetailsPage(blockData.id)}>
      <td className="text-center">
        <div className="c-avatar">
          <strong>{rowIndex + 1}</strong>
        </div>
      </td>
      <td>
        <strong>{blockData.id}</strong>
        <div className="small text-muted">
          <span>New</span> | Registered: <strong>{moment(blockData.attributes.datetime).format("LTS")}</strong>
        </div>
      </td>
      <td className="text-center">
        <div>
          <strong>{blockData.attributes.count_extrinsics_signed}</strong>
        </div>
      </td>
      <td>
        <div className="small text-muted">Last block created</div>
        <strong>
          <ReactTimeAgo
            date={Date.parse(blockData.attributes.datetime)}
            locale="en-US"
            timeStyle="round"
          />
        </strong>
      </td>
    </tr>
  )

  renderTransactionTableRow = (transactionData, rowIndex) => (
    <tr key={rowIndex} onClick={() => this.navigateToTransactionDetailsPage(transactionData.id)}>
      <td className="text-center">
        <div className="c-avatar">
          <strong>{rowIndex + 1}</strong>
        </div>
      </td>
      <td>
        <div>
          <strong>{transactionData.attributes.sender.attributes.address}</strong>
        </div>
        <div className="small text-muted">
          <span>Block</span> | Added: <strong>{transactionData.id}</strong>
        </div>
      </td>
      <td className="text-center">
        <div>
          <strong>{transactionData.attributes.destination.attributes.address}</strong>
        </div>
      </td>
      <td>
        <div className="small text-muted">Last block created</div>
        <strong>{transactionData.attributes.value / METAMUI_TOKEN_DECIMALS} MetaMUI</strong>
      </td>
    </tr>
  )

  renderResponsiveDataTable = (tableTitle, columnHeaders, tableData, rowRenderFunction) => (
    <CCard>
      <CCardHeader>
        <strong>{tableTitle}</strong>
      </CCardHeader>
      <CCardBody>
        <table className="table table-hover table-outline mb-0 table-responsive d-sm-table">
          <thead className="thead-light">
            <tr>
              <th className="text-center">
                <CIcon name="cil-people" />
              </th>
              {columnHeaders.map((headerConfig, headerIndex) => (
                <th key={headerIndex} className={headerConfig.className || ''}>
                  {headerConfig.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map(rowRenderFunction)}
          </tbody>
        </table>
      </CCardBody>
    </CCard>
  )

  render() {
    const blocksTableColumnHeaders = [
      { title: 'Block No' },
      { title: 'Transactions', className: 'text-center' },
      { title: 'Activity' }
    ];

    const transactionsTableColumnHeaders = [
      { title: 'Sender' },
      { title: 'Receiver', className: 'text-center' },
      { title: 'Amount' }
    ];

    return (
      <div>
        <WidgetsDropdown />
        
        <CRow>
          <CCol>
            {this.renderResponsiveDataTable(
              'Blocks & Transactions',
              blocksTableColumnHeaders,
              this.state.recentBlocksList,
              this.renderBlockTableRow
            )}
            
            <br />
            
            {this.renderResponsiveDataTable(
              'MetaMUI Transactions',
              transactionsTableColumnHeaders,
              this.state.recentTransactionsList,
              this.renderTransactionTableRow
            )}
          </CCol>
        </CRow>
      </div>
    );
  }
}

export default withRouter(Dashboard);
